import { useTranslation } from "@sagot/i18n";
import {
  Button,
  DatePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Segmented,
  Select,
  toast,
} from "@sagot/ui";
import { useState } from "react";
import { usePermissions } from "../../../session/permissions";
import { datedFilename, saveBlob } from "../shared/download";
import { useAgentList } from "../shared/use-engage-data";
import { ANALYTICS_CHANNELS, RANGE_PRESETS, isRangeValid, type AnalyticsFilters, type ChannelType, type RangePreset } from "./analytics-model";
import { useExportCsv } from "./use-analytics";

interface FiltersProps {
  filters: AnalyticsFilters;
  onChange: (next: AnalyticsFilters) => void;
}

function ExportMenu({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const exportCsv = useExportCsv();
  const [busy, setBusy] = useState(false);
  if (!permissions.can("analytics.export", { anywhere: true })) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { blob, filename } = await exportCsv(filters);
      const name = filename ?? datedFilename("analytics", "csv");
      saveBlob(blob, name);
      toast.success(t("engage.analytics.export.done", { file: name }));
    } catch {
      toast.error(t("engage.exportFailed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" loading={busy} disabled={!isRangeValid(filters)}>
          <Icon name="download" />
          {t("engage.analytics.export.menu")}
          <Icon name="chevron-down" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={() => void run()}>
          <Icon name="file-spreadsheet" className="size-4" />
          {t("engage.analytics.export.csv")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Range Segmented (+ custom dates), agent + channel selects, export menu. */
export function AnalyticsFiltersBar({ filters, onChange }: FiltersProps) {
  const { t } = useTranslation();
  const agents = useAgentList();
  const rangeLabel = (r: RangePreset) => (r === "custom" ? t("engage.analytics.filters.custom") : t(`common.timeRange.${r}`));
  const invalid = !isRangeValid(filters) && Boolean(filters.from && filters.to);
  return (
    <div className="flex flex-col gap-2 xl:shrink-0 xl:items-end">
      <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap">
        <Segmented
          aria-label={t("engage.analytics.filters.range")}
          value={filters.range}
          onValueChange={(range) => {
            onChange({ ...filters, range });
          }}
          options={RANGE_PRESETS.map((r) => ({ value: r, label: rangeLabel(r) }))}
        />
        <Select
          className="h-9 w-48"
          aria-label={t("engage.analytics.filters.agent")}
          value={filters.agentId ?? ""}
          onChange={(e) => {
            onChange({ ...filters, agentId: e.target.value || null });
          }}
        >
          <option value="">{t("engage.allAgents")}</option>
          {(agents.data ?? []).map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name}
            </option>
          ))}
        </Select>
        <Select
          className="h-9 w-40"
          aria-label={t("engage.analytics.filters.channel")}
          value={filters.channel ?? ""}
          onChange={(e) => {
            onChange({ ...filters, channel: (e.target.value || null) as ChannelType | null });
          }}
        >
          <option value="">{t("engage.allChannels")}</option>
          {ANALYTICS_CHANNELS.map((channel) => (
            <option key={channel} value={channel}>
              {t(`engage.channels.${channel}`)}
            </option>
          ))}
        </Select>
        <ExportMenu filters={filters} />
      </div>
      {filters.range === "custom" ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <DatePicker
            className="h-9 w-40"
            aria-label={t("engage.analytics.filters.from")}
            value={filters.from}
            max={filters.to || undefined}
            onValueChange={(from) => {
              onChange({ ...filters, from });
            }}
          />
          <span aria-hidden="true" className="text-fg-subtle">
            –
          </span>
          <DatePicker
            className="h-9 w-40"
            aria-label={t("engage.analytics.filters.to")}
            value={filters.to}
            min={filters.from || undefined}
            onValueChange={(to) => {
              onChange({ ...filters, to });
            }}
          />
          {invalid ? (
            <p role="alert" className="flex items-center gap-1 text-xs font-semibold text-danger">
              <Icon name="alert-circle" className="size-3.5" />
              {t("engage.analytics.filters.invalidRange")}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
